import SlideFrame from "@/components/SlideFrame";
import {
  ShieldCheck, AlertTriangle, Lock, Network, Server, Quote, Building2, Award, Sparkles,
  Phone, Calendar, Mail, Check, X, ArrowRight, Briefcase, Scale, FileSearch, Megaphone,
  ClipboardList, GraduationCap, BookOpen, Users, AlertOctagon, TrendingUp, HelpCircle, Sliders
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

/* ============================ 11 · Trusted By + Quotes ============================ */
export function S_Trusted({ idx, total }) {
  const logos = [
    { name: "Checkbook",              src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/491dq3mt_Checkbook.logo.png",            href: "https://checkbook.io/" },
    { name: "BuckUp",                 src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/2z2lkeku_buckup2.0.png",                  href: "https://gobuckup.com/" },
    { name: "CLKK",                   src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/tqqg6ar1_clkk.logo.png",                  href: "https://www.clkk.io/" },
    { name: "Covalent Resource Group",src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/d3h05bvk_covalent.logo.png",              href: "https://www.covalentrg.com/", dark: true },
    { name: "Glep",                   src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/7aqzztfu_GlepLogo.png",                   href: "https://glep.com/" },
    { name: "InStore Finance",        src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/1dgeag25_InStore.logo.png",               href: "https://instorefinance.com/index.php" },
    { name: "Pointe Capital",         src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/adviignu_LogoPointeCapital%20Large.png",  href: "https://www.pointecapitalmanagement.com/" },
    { name: "RNPL",                   src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/djir7f23_rnpl.logo.png",                  href: "https://rnplcredit.com/" },
    { name: "Vault Bank",             src: "https://customer-assets.emergentagent.com/job_guardian-compliance/artifacts/tl1js9a3_VaultLogo.png",                  href: "https://www.vault.bank/" },
    { name: "Alley Analytix",         src: "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/v4264gbp_AlleyLOGO.png",                      href: "https://alleyanalytix.com/" },
  ];
  const quotes = [
    {
      q: "You need the right partner. We chose the right partner with CompliSun, and I honestly don't see bringing in a whole Compliance Department again. With CompliSun, the work gets done, no sacrifices, and far better quality than one would get from most fulltime hires.",
      who: "Pia Thompson",
      role: "CEO, Checkbook",
    },
    {
      q: "Partnering with CompliSun Guardian was instrumental in strengthening our compliance management systems. It significantly reduced both cost and time when implementing marketing audits and securing our lending licenses.",
      who: "Mark Mughabghab",
      role: "CEO, RNPL Credit",
    },
  ];
  return (
    <SlideFrame idx={idx} total={total} kicker="Trusted By" dark={false}>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-3">10 · trusted by</p>
          <h2 className="serif font-light text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Industry leaders trust <em className="italic text-sun-600">CompliSun</em>.
          </h2>
        </div>
        <div className="flex items-center gap-3 bg-cream-50 text-ink-900 rounded-md px-5 py-3" data-testid="award-badge">
          <Award className="h-5 w-5 text-sun-500" strokeWidth={1.5} />
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/65">LendAPI Partner of the Year</div>
            <div className="serif text-base">2025 · Excellence in Compliance</div>
          </div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-cream-50/8 border border-ink-900/10 rounded-md overflow-hidden">
        {logos.map((l) => (
          <a
            key={l.name}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`${l.dark ? "bg-ink-900 hover:bg-ink-800" : "bg-cream-50 hover:bg-cream-200"} aspect-[3/1.4] flex items-center justify-center p-5 transition-colors group`}
            data-testid={`logo-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
            aria-label={`Visit ${l.name}`}
          >
            <img src={l.src} alt={l.name} className="max-h-full max-w-full object-contain opacity-85 group-hover:opacity-100 transition-opacity" loading="lazy" />
          </a>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 stagger">
        {quotes.map((q, i) => (
          <figure key={q.who} className="border-l-2 border-sun-500 pl-6" data-testid={`testimonial-${i}`}>
            <Quote className="h-6 w-6 text-sun-600/70 mb-3" strokeWidth={1.2} />
            <blockquote className="serif text-base md:text-lg text-ink-900/85 leading-snug italic">
              "{q.q}"
            </blockquote>
            <figcaption className="mt-4">
              <div className="serif text-base text-ink-900">{q.who}</div>
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mt-0.5">{q.role}</div>
            </figcaption>
          </figure>
        ))}
      </div>

      <p className="mt-7 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55" data-testid="case-studies-note">
        / fintech CMS + bank-partnership win · RIA through SEC onsite · multi-state CMS expansion ·
        <span className="text-sun-700"> request the full case studies under NDA → colindarke@complisun.com</span>
      </p>
    </SlideFrame>
  );
}
